import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Connection,
  addEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Maximize2 } from 'lucide-react';
import { ChemExperimentState, MaterialLine, ReactionStep } from './types';
import { loadRDKit } from './rdkitLoader';

interface ReactionPathViewProps {
  experiment: ChemExperimentState;
}

/**
 * ReactionPathView Component
 * 
 * Phase 4: Visualizes reaction paths using React Flow
 * 
 * Features:
 * - Material nodes with structure info (MW, formula)
 * - Reaction step nodes
 * - Edges showing reactant → reaction → product flow
 * - RDKit.js validation of structural relationships
 */
export const ReactionPathView: React.FC<ReactionPathViewProps> = ({ experiment }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStep, setSelectedStep] = useState<ReactionStep | null>(null);
  const [validationResults, setValidationResults] = useState<Record<string, {
    hasMatch: boolean;
    matchCount: number;
    error?: string;
  }>>({});
  const fullscreenWindowRef = useRef<Window | null>(null);

  /**
   * Validate reaction steps using RDKit.js substructure matching
   * For each step, check if reactant substructures are found in products
   */
  const validateSteps = useCallback(async (steps: ReactionStep[], RDKit: any, materials: MaterialLine[]): Promise<void> => {
    const results: Record<string, {
      hasMatch: boolean;
      matchCount: number;
      error?: string;
    }> = {};

    for (const step of steps) {
      try {
        // Get first reactant and first product for validation
        const reactantId = step.reactantMaterialIds[0];
        const productId = step.productMaterialIds[0];

        if (!reactantId || !productId) {
          results[step.id] = {
            hasMatch: false,
            matchCount: 0,
            error: 'Missing reactant or product',
          };
          continue;
        }

        const reactant = materials.find((m) => m.id === reactantId);
        const product = materials.find((m) => m.id === productId);

        if (!reactant || !product) {
          results[step.id] = {
            hasMatch: false,
            matchCount: 0,
            error: 'Material not found',
          };
          continue;
        }

        const reactantSmiles = reactant.structure?.smiles;
        const productSmiles = product.structure?.smiles;

        if (!reactantSmiles || !productSmiles) {
          results[step.id] = {
            hasMatch: false,
            matchCount: 0,
            error: 'Missing SMILES structures',
          };
          continue;
        }

        // Parse molecules
        const reactantMol = RDKit.get_mol(reactantSmiles);
        const productMol = RDKit.get_mol(productSmiles);

        if (!reactantMol || !productMol) {
          results[step.id] = {
            hasMatch: false,
            matchCount: 0,
            error: 'Failed to parse structures',
          };
          continue;
        }

        // Check if reactant substructure is found in product
        const match = productMol.get_substruct_match(reactantMol);
        const hasMatch = match && match.length > 0;
        const matchCount = hasMatch ? match.length : 0;

        results[step.id] = {
          hasMatch,
          matchCount,
        };

        // Clean up molecules
        reactantMol.delete();
        productMol.delete();
      } catch (err) {
        console.error(`Validation error for step ${step.id}:`, err);
        results[step.id] = {
          hasMatch: false,
          matchCount: 0,
          error: `Validation error: ${err}`,
        };
      }
    }

    setValidationResults(results);
  }, []);

  // Build graph from experiment steps
  useEffect(() => {
    const buildGraph = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!experiment.steps || experiment.steps.length === 0) {
          setIsLoading(false);
          return;
        }

        // Load RDKit for structure validation
        let RDKit: any = null;
        try {
          RDKit = await loadRDKit();
        } catch (err) {
          console.warn('RDKit not available, proceeding without structure validation');
        }

        const graphNodes: Node[] = [];
        const graphEdges: Edge[] = [];
        
        // Layout constants
        const ySpacing = 150;
        const xSpacing = 300; // Spacing between each step group (reactants + reaction)
        const reactantReactionGap = 200; // Gap between reactants and their reaction step
        
        // Track all products to position them at the end
        const allProducts = new Map<string, MaterialLine>();
        
        // Process each reaction step sequentially
        let currentX = 0;
        
        experiment.steps.forEach((step, stepIndex) => {
          // Get reactants for this step
          const stepReactants = step.reactantMaterialIds
            .map((id) => experiment.materials.find((m) => m.id === id))
            .filter((m): m is MaterialLine => m !== undefined);
          
          // Get products for this step
          const stepProducts = step.productMaterialIds
            .map((id) => experiment.materials.find((m) => m.id === id))
            .filter((m): m is MaterialLine => m !== undefined);
          
          // Collect all products (will be positioned at the end)
          stepProducts.forEach((material) => {
            if (!allProducts.has(material.id)) {
              allProducts.set(material.id, material);
            }
          });
          
          // Step 1: Position reactants for this step (grouped with the step)
          stepReactants.forEach((material, matIndex) => {
            const materialNodeId = `material-${material.id}`;
            
            // Check if this material node already exists (might be a product from previous step)
            const existingNode = graphNodes.find((n) => n.id === materialNodeId);
            
            if (!existingNode) {
              // Create new reactant node positioned just before this reaction step
              const materialNode: Node = {
                id: materialNodeId,
                type: 'default',
                position: {
                  x: currentX,
                  y: matIndex * ySpacing - (stepReactants.length - 1) * ySpacing / 2,
                },
                data: {
                  label: (
                    <div style={styles.materialNode}>
                      <div style={styles.materialName}>{material.name}</div>
                      {material.structure?.formula && (
                        <div style={styles.materialFormula}>{material.structure.formula}</div>
                      )}
                      {material.structure?.molWeight && (
                        <div style={styles.materialMW}>
                          MW: {material.structure.molWeight.toFixed(1)} g/mol
                        </div>
                      )}
                      <div style={styles.materialRole}>{material.role}</div>
                    </div>
                  ),
                },
                style: {
                  backgroundColor: '#f0fdf4',
                  border: '2px solid #10b981',
                  borderRadius: '8px',
                  padding: '12px',
                  minWidth: '180px',
                },
              };
              graphNodes.push(materialNode);
            }
            
            // Create edge: reactant → reaction
            const reactionNodeId = `reaction-${step.id}`;
            graphEdges.push({
              id: `edge-${materialNodeId}-${reactionNodeId}`,
              source: materialNodeId,
              target: reactionNodeId,
              type: 'smoothstep',
              animated: true,
              style: { stroke: '#10b981', strokeWidth: 2 },
            });
          });
          
          // Step 2: Position reaction step (adjacent to its reactants)
          const reactionNodeId = `reaction-${step.id}`;
          const reactionX = currentX + reactantReactionGap;
          
          const reactionNode: Node = {
            id: reactionNodeId,
            type: 'default',
            position: { x: reactionX, y: 0 },
            data: {
              label: (
                <div style={styles.reactionNode}>
                  <div style={styles.reactionTitle}>{step.name}</div>
                  <div style={styles.reactionType}>{step.type}</div>
                  {step.conditions && (
                    <div style={styles.reactionConditions}>
                      {step.conditions.temperature && `T: ${step.conditions.temperature}`}
                      {step.conditions.time && ` | Time: ${step.conditions.time}`}
                    </div>
                  )}
                </div>
              ),
            },
            style: {
              backgroundColor: '#eff6ff',
              border: '2px solid #2563eb',
              borderRadius: '8px',
              padding: '12px',
              minWidth: '200px',
            },
          };
          graphNodes.push(reactionNode);
          
          // Step 3: Create edges from reaction to products (products will be positioned later)
          stepProducts.forEach((product) => {
            const productNodeId = `material-${product.id}`;
            graphEdges.push({
              id: `edge-${reactionNodeId}-${productNodeId}`,
              source: reactionNodeId,
              target: productNodeId,
              type: 'smoothstep',
              animated: true,
              style: { stroke: '#f59e0b', strokeWidth: 2 },
            });
          });
          
          // Move to next step group position
          currentX += xSpacing;
        });
        
        // Step 4: Position all products on the far right (after all steps)
        const productArray = Array.from(allProducts.values());
        const productStartX = currentX + 200; // After all reaction steps
        
        productArray.forEach((material, index) => {
          const materialNodeId = `material-${material.id}`;
          
          // Check if this product node already exists (might be a reactant for a later step)
          const existingNode = graphNodes.find((n) => n.id === materialNodeId);
          
          if (!existingNode) {
            // Create new product node
            const materialNode: Node = {
              id: materialNodeId,
              type: 'default',
              position: {
                x: productStartX,
                y: index * ySpacing - (productArray.length - 1) * ySpacing / 2,
              },
              data: {
                label: (
                  <div style={styles.materialNode}>
                    <div style={styles.materialName}>{material.name}</div>
                    {material.structure?.formula && (
                      <div style={styles.materialFormula}>{material.structure.formula}</div>
                    )}
                    {material.structure?.molWeight && (
                      <div style={styles.materialMW}>
                        MW: {material.structure.molWeight.toFixed(1)} g/mol
                      </div>
                    )}
                    <div style={styles.materialRole}>{material.role}</div>
                  </div>
                ),
              },
              style: {
                backgroundColor: '#fef3c7',
                border: '2px solid #f59e0b',
                borderRadius: '8px',
                padding: '12px',
                minWidth: '180px',
              },
            };
            graphNodes.push(materialNode);
          } else {
            // Update existing node position if it's a product (might have been created as reactant)
            // Only update if it's not already positioned as a product
            if (existingNode.position.x < productStartX - 100) {
              existingNode.position.x = productStartX;
              existingNode.position.y = index * ySpacing - (productArray.length - 1) * ySpacing / 2;
            }
          }
        });

        setNodes(graphNodes);
        setEdges(graphEdges);
        setIsLoading(false);
        
        // Perform RDKit validation for all steps (async, don't wait)
        if (RDKit && experiment.steps) {
          validateSteps(experiment.steps, RDKit, experiment.materials).catch((err) => {
            console.error('Validation error:', err);
          });
        }
      } catch (err) {
        console.error('Error building reaction path:', err);
        setError(`Failed to build reaction path: ${err}`);
        setIsLoading(false);
      }
    };

    buildGraph();
  }, [experiment]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading reaction path...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!experiment.steps || experiment.steps.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No reaction steps defined</p>
          <p style={styles.emptySubtext}>
            Add reaction steps in the Reaction Setup tab to visualize the reaction path
          </p>
        </div>
      </div>
    );
  }

  /**
   * Open fullscreen popup window (like KetcherModal)
   * Opens a separate popup window with the reaction path graph
   */
  const handleOpenFullscreen = () => {
    const width = 1400;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    // Create a new popup window
    const popup = window.open(
      '',
      'ReactionPathFullscreen',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      alert('Popup blocked. Please allow popups for this site.');
      return;
    }

    fullscreenWindowRef.current = popup;

    // Serialize nodes and edges to pass to popup
    const graphData = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    };

    // Create HTML content with embedded React Flow via CDN
    // This is a simplified approach - for production, you'd want a proper React bundle
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Reaction Path - Fullscreen</title>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; overflow: hidden; }
            #root { width: 100vw; height: 100vh; display: flex; flex-direction: column; }
            .header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
            .header h2 { margin: 0; font-size: 20px; font-weight: 600; color: #1e293b; }
            .close-btn { background: none; border: none; font-size: 32px; cursor: pointer; color: #64748b; padding: 4px 12px; border-radius: 4px; line-height: 1; }
            .close-btn:hover { background: #e2e8f0; }
            .flow-wrapper { flex: 1; width: 100%; height: 100%; }
          </style>
          <link rel="stylesheet" href="https://unpkg.com/reactflow@11/dist/style.css">
        </head>
        <body>
          <div id="root">
            <div class="header">
              <h2>Reaction Path Visualization</h2>
              <button class="close-btn" onclick="window.close()">×</button>
            </div>
            <div class="flow-wrapper" id="flow-container"></div>
          </div>
          <script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
          <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
          <script src="https://unpkg.com/reactflow@11/dist/umd/index.js"></script>
          <script>
            const graphData = ${JSON.stringify(graphData)};
            const { ReactFlow, Background, Controls, MiniMap } = window.ReactFlow;
            const React = window.React;
            const ReactDOM = window.ReactDOM;
            
            // Render React Flow in popup
            const container = document.getElementById('flow-container');
            const root = ReactDOM.createRoot(container);
            
            root.render(
              React.createElement(ReactFlow.default || ReactFlow, {
                nodes: graphData.nodes,
                edges: graphData.edges,
                fitView: true,
                children: [
                  React.createElement(Background.default || Background),
                  React.createElement(Controls.default || Controls),
                  React.createElement(MiniMap.default || MiniMap)
                ]
              })
            );
          </script>
        </body>
      </html>
    `;

    // Write content to popup
    popup.document.write(htmlContent);
    popup.document.close();

    // Clean up when popup closes
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        fullscreenWindowRef.current = null;
        clearInterval(checkClosed);
      }
    }, 500);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h3 style={styles.heading}>Reaction Path Visualization</h3>
          <p style={styles.subheading}>
            {experiment.steps.length} step{experiment.steps.length !== 1 ? 's' : ''} defined
          </p>
        </div>
        <button
          style={styles.fullscreenButton}
          onClick={handleOpenFullscreen}
          title="Open in fullscreen popup"
        >
          <Maximize2 size={18} />
          Fullscreen
        </button>
      </div>
      
      {/* Step List with Validation */}
      <div style={styles.stepsList}>
        {experiment.steps.map((step) => {
          const validation = validationResults[step.id];
          const reactantNames = step.reactantMaterialIds
            .map((id) => experiment.materials.find((m) => m.id === id)?.name)
            .filter(Boolean)
            .join(', ') || 'None';
          
          const productNames = step.productMaterialIds
            .map((id) => experiment.materials.find((m) => m.id === id)?.name)
            .filter(Boolean)
            .join(', ') || 'None';

          const hasStructures = [
            ...step.reactantMaterialIds,
            ...step.productMaterialIds,
          ].every((id) => {
            const material = experiment.materials.find((m) => m.id === id);
            return !!(material?.structure?.smiles || material?.structure?.molfile);
          });

          return (
            <div
              key={step.id}
              style={{
                ...styles.stepCard,
                ...(selectedStep?.id === step.id ? styles.stepCardSelected : {}),
              }}
              onClick={() => setSelectedStep(step)}
            >
              <div style={styles.stepHeader}>
                <div>
                  <div style={styles.stepName}>{step.name}</div>
                  <div style={styles.stepType}>{step.type}</div>
                </div>
                <div style={styles.stepBadges}>
                  {hasStructures ? (
                    <span style={styles.badgeOk}>Structure OK</span>
                  ) : (
                    <span style={styles.badgeWarning}>Missing structure</span>
                  )}
                  {validation && (
                    <span
                      style={
                        validation.hasMatch
                          ? styles.badgeOk
                          : styles.badgeError
                      }
                    >
                      {validation.hasMatch
                        ? `✓ Mapping OK (${validation.matchCount} atoms)`
                        : `⚠ No match${validation.error ? `: ${validation.error}` : ''}`}
                    </span>
                  )}
                </div>
              </div>
              <div style={styles.stepDetails}>
                <div style={styles.stepReactants}>
                  <strong>Reactants:</strong> {reactantNames}
                </div>
                <div style={styles.stepProducts}>
                  <strong>Products:</strong> {productNames}
                </div>
                {step.conditions && (
                  <div style={styles.stepConditions}>
                    <strong>Conditions:</strong>{' '}
                    {[
                      step.conditions.temperature && `T: ${step.conditions.temperature}`,
                      step.conditions.pressure && `P: ${step.conditions.pressure}`,
                      step.conditions.time && `Time: ${step.conditions.time}`,
                      step.conditions.pH && `pH: ${step.conditions.pH}`,
                    ]
                      .filter(Boolean)
                      .join(' | ')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Graph Visualization */}
      <div style={styles.flowContainer}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          fitView
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    marginBottom: '16px',
  },
  heading: {
    margin: '0 0 4px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  subheading: {
    margin: 0,
    fontSize: '13px',
    color: '#64748b',
  },
  flowContainer: {
    flex: 1,
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #2563eb',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '14px',
    color: '#64748b',
  },
  errorBox: {
    padding: '16px',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#991b1b',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyText: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: 500,
    color: '#64748b',
  },
  emptySubtext: {
    margin: 0,
    fontSize: '14px',
    color: '#94a3b8',
  },
  materialNode: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  materialName: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#1e293b',
  },
  materialFormula: {
    fontSize: '12px',
    fontFamily: 'monospace',
    color: '#2563eb',
  },
  materialMW: {
    fontSize: '11px',
    color: '#64748b',
  },
  materialRole: {
    fontSize: '11px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  reactionNode: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  reactionTitle: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#1e293b',
  },
  reactionType: {
    fontSize: '12px',
    color: '#64748b',
  },
  reactionConditions: {
    fontSize: '11px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  stepsList: {
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  stepCard: {
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  stepCardSelected: {
    borderColor: '#2563eb',
    backgroundColor: '#eff6ff',
  },
  stepHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  stepName: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#1e293b',
    marginBottom: '4px',
  },
  stepType: {
    fontSize: '12px',
    color: '#64748b',
  },
  stepBadges: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'flex-end',
  },
  badgeOk: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    fontWeight: 500,
  },
  badgeWarning: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    fontWeight: 500,
  },
  badgeError: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    fontWeight: 500,
  },
  stepDetails: {
    fontSize: '12px',
    color: '#475569',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  stepReactants: {
    marginBottom: '4px',
  },
  stepProducts: {
    marginBottom: '4px',
  },
  stepConditions: {
    fontSize: '11px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  fullscreenOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullscreenContent: {
    width: '95vw',
    height: '95vh',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  fullscreenHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  fullscreenTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#1e293b',
  },
  fullscreenCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    cursor: 'pointer',
    color: '#64748b',
    padding: '4px 12px',
    borderRadius: '4px',
    lineHeight: 1,
    transition: 'background-color 0.2s',
  },
  fullscreenFlowContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
};

// Add spinner animation
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('reaction-path-spinner-style');
  if (!existingStyle) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'reaction-path-spinner-style';
    styleSheet.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleSheet);
  }
}

